import React from 'react';
import { useDomainGroups } from '../../../../Context/DomainGroupsContext';
import { agentGroupNames } from '../../../../Utils';
import { Card, Timeline, Tag, Typography, Avatar, Badge, Empty, Collapse, Descriptions, Button, Tooltip, Divider, Space } from 'antd';
import {
    SwapOutlined, DollarOutlined, FileSearchOutlined,
    EnvironmentOutlined, CarOutlined, UserSwitchOutlined,
    HistoryOutlined, CalendarOutlined, CheckCircleOutlined,
    PlusOutlined, UserOutlined, MessageOutlined,
    CommentOutlined, PhoneOutlined, MailOutlined,
    CreditCardOutlined, HomeOutlined, GlobalOutlined,
    ChromeOutlined, LaptopOutlined, ClusterOutlined,
    EyeOutlined, SearchOutlined, ClockCircleOutlined,
    DownloadOutlined, TransactionOutlined, ProfileOutlined,
    SendOutlined, PrinterOutlined,
    LockOutlined,
    TeamOutlined,
    MailFilled,
    PhoneFilled,
     TagOutlined,
    AppstoreOutlined,
    TrophyOutlined,
    StarOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Helper function to format date (you should import this from utils)
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
    });
};

const getActivityTagColor = (activityType, stage = null) => {
    // Special case for Payment Completed
    if (stage === 'Payment Completed') {
        return 'green';
    }
    
    switch (activityType) {
        case 'payment': return 'green';
        case 'quote': return 'blue';
        case 'stage': return 'purple';
        case 'address': return 'orange';
        case 'yard': return 'cyan';
        case 'transfer': return 'magenta';
        case 'followup': return 'gold';
        case 'order': return 'success';
        default: return 'default';
    }
};

const REVIEW_LEAD_RECOVERED_BY_PREFIX = 'Recovered from Review Lead by';

const isAdminOnlyLeadStageHistoryEntry = (entry = {}) => (
    entry.isAdminOnly === true ||
    (typeof entry.comment === 'string' && entry.comment.trim().startsWith(REVIEW_LEAD_RECOVERED_BY_PREFIX))
);

const getVisibleLeadStageHistory = (history = [], isAdmin = false) => {
    const items = Array.isArray(history) ? history : [];
    return isAdmin ? items : items.filter((entry) => !isAdminOnlyLeadStageHistoryEntry(entry));
};

// Main function to get all activities
const getAllActivities = (lead, agentMap) => {
    const activities = [];

    if (!lead) return activities;

  // 1. Add stage changes
if (lead.leadStageHistory && lead.leadStageHistory.length > 0) {
    lead.leadStageHistory.forEach(history => {
        // Check if stage is Payment Completed
        const isPaymentCompleted = history.stage === 'Payment Completed';
        
        activities.push({
            type: 'stage',
            title: isPaymentCompleted ? 'Stage Changed to Payment Details Received' : `Stage Changed to ${history.stage}`,
            description: isPaymentCompleted 
                ? (history.comment || 'Payment details have been received and completed')
                : (history.comment || `Stage changed to ${history.stage}`),
            timestamp: history.changedAt,
            icon: <SwapOutlined />,
            user: history.userName || 'System',
            stage: history.stage
        });
    });
}
    // Sort all activities by timestamp (newest first)
    return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

const ActivityHistoryTab = ({ 
    lead,
    agentMap,
    agentDetails = {},
    isAgentAssigned,
    userData,
    onTransferAgent, // Keep prop but don't use it for actions
}) => {
    // The groups an agent's memberships resolve to, for the labels below.
    const { groups } = useDomainGroups();
    // Helper function to resolve agent ID
    const resolveAgentId = (agentRef) => {
        if (!agentRef) return null;
        if (typeof agentRef === 'string') return agentRef;
        return agentRef._id || agentRef.id || null;
    };

    // Helper function to get agent details
    const getAgentDetails = (agentRef) => {
        const agentId = resolveAgentId(agentRef);

        if (agentId && agentDetails && agentDetails[agentId]) {
            const agent = agentDetails[agentId];
          return {
    name: `${agent.firstName} ${agent.lastName}` || "No Agent",
    email: agent.email || `${agentId}@company.com`,
    phone: agent.phone || 'Not available',
    role: agent.role || 'Queue',
    avatarInitial: (agent.firstName?.charAt(0) || agent.userName?.charAt(0) || 'C').toUpperCase(),
    status: agent.status || 'ACTIVE',
    extension: agent.extension || 'N/A',
    userName: agent.userName || 'N/A',
    directNumber: agent.directNumber || 'N/A',
    subRole: agent.subRole || 'N/A',
    isPerformance: agent.isPerformance || false,
    domainGroups: agent.domainGroups || [],
    subset1: agent.subset1 || false,
    subset2: agent.subset2 || false,
    isDefaultTierEligible: agent.isDefaultTierEligible !== false,
    premium: agent.premium || false
};
        }

        if (agentRef && typeof agentRef === 'object') {
            const fullName = [agentRef.firstName, agentRef.lastName].filter(Boolean).join(' ').trim();
            const name = fullName || agentRef.userName || (agentId ? agentMap[agentId] : null) || 'Unknown Agent';
return {
    name,
    email: agentRef.email || (agentId ? `${agentId}@company.com` : 'Not available'),
    phone: agentRef.directNumber || agentRef.phone || 'Not available',
    role: agentRef.role || 'SALES-AGENT',
    avatarInitial: (
        agentRef.firstName?.charAt(0) ||
        agentRef.userName?.charAt(0) ||
        name.charAt(0) ||
        'A'
    ).toUpperCase(),
    status: agentRef.status || (agentRef.isActive === false ? 'INACTIVE' : 'ACTIVE'),
    extension: agentRef.extension || 'N/A',
    userName: agentRef.userName || 'N/A',
    directNumber: agentRef.directNumber || 'N/A',
    subRole: agentRef.subRole || 'N/A',
    isPerformance: agentRef.isPerformance || false,
    domainGroups: agentRef.domainGroups || [],
    subset1: agentRef.subset1 || false,
    subset2: agentRef.subset2 || false,
    isDefaultTierEligible: agentRef.isDefaultTierEligible !== false,
    premium: agentRef.premium || false
};
        }

   const fallbackName = (agentId && agentMap[agentId]) || 'Unknown Agent';
return {
    name: fallbackName,
    email: agentId ? `${agentId}@company.com` : 'Not available',
    phone: 'Not available',
    role: 'SALES-AGENT',
    avatarInitial: fallbackName.charAt(0).toUpperCase(),
    status: 'ACTIVE',
    extension: 'N/A',
    userName: fallbackName,
    directNumber: 'Not available',
    subRole: 'N/A',
    isPerformance: false,
    domainGroups: [],
    subset1: false,
    subset2: false,
    isDefaultTierEligible: false,
    premium: false
};
    };
const currentUserRole = userData?.role; // or wherever you get user role from
const isAdmin = currentUserRole === 'ADMIN' ;
    const visibleLeadStageHistory = getVisibleLeadStageHistory(lead?.leadStageHistory, isAdmin);

    // If no agent assigned, show assignment prompt (view-only version)
    if (!isAgentAssigned || !lead?.handledBy) {
        return (
            <Card
                style={{
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(135deg, #fff9e6 0%, #ffe6b3 100%)',
                    border: '1px solid #ffd700'
                }}
            >
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <UserOutlined style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }} />
                    <Title level={4} style={{ color: '#faad14', marginBottom: '8px' }}>
                        No Agent Assigned
                    </Title>
                    <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                        This lead is currently not assigned to any sales agent.
                    </Text>
                </div>
                <Card style={{ marginTop: '20px' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <Title level={4}>Activity History</Title>
                        <Text type="secondary">
                            Showing all activities in chronological order
                            <Badge
                                count={0}
                                size="small"
                                style={{ marginLeft: '8px', backgroundColor: '#1890ff' }}
                            />
                        </Text>
                    </div>
                    <Empty
                        description="No activities found"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                </Card>
            </Card>
        );
    }

    // Get current agent details
    const currentAgent = getAgentDetails(lead.handledBy);
    const visibleLead = {
        ...lead,
        leadStageHistory: visibleLeadStageHistory
    };
    const activities = getAllActivities(visibleLead, agentMap);

 
    const calculateActivityCount = (lead) => {
        if (!lead) return 0;
        let count = 0;
        count += getVisibleLeadStageHistory(lead.leadStageHistory, isAdmin).length || 0;
        count += lead.advancePayments?.length || 0;
        count += lead.quotedValue?.length || 0;
        count += lead.transferHistory?.length || 0;
        count += lead.yard?.length || 0;
        return count;
    };

    return (
        <>
            <Card
                style={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%)',
                    border: '1px solid #91d5ff'
                }}
            >
          

                {/* Agent Details Section - View Only */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '24px',
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    flexWrap: 'wrap'
                }}>
             

                    {/* Agent Info Section */}
                    <div style={{ flex: 1, minWidth: '200px' }}>
                        <div style={{ marginBottom: '12px' }}>
                            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                                {currentAgent.name}
                            </Title>
                            <Tag
                                color="blue"
                                icon={<TeamOutlined />}
                                style={{ fontSize: '12px', marginTop: '8px', padding: '2px 12px' }}
                            >
                                {currentAgent.role}
                            </Tag>
                        </div>

                        <Divider style={{ margin: '16px 0' }} />

                     <Space size="large" wrap style={{ rowGap: '12px' }}>
    {/* Basic Info - Everyone can see */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <MailFilled style={{ color: '#1890ff', fontSize: '16px' }} />
        </div>
        <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>Email</Text>
            <div><Text strong>{currentAgent.email}</Text></div>
        </div>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PhoneFilled style={{ color: '#1890ff', fontSize: '16px' }} />
        </div>
        <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>Phone</Text>
            <div><Text strong>{currentAgent.phone || currentAgent.directNumber}</Text></div>
        </div>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <UserOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
        </div>
        <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>Username</Text>
            <div><Text strong>{currentAgent.userName}</Text></div>
        </div>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PhoneOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
        </div>
        <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>Extension</Text>
            <div><Text strong>{currentAgent.extension}</Text></div>
        </div>
    </div>

    {isAdmin && (
        <>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f9f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <TagOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
                </div>
                <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Sub Role</Text>
                    <div><Text strong>{currentAgent.subRole}</Text></div>
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f9f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircleOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
                </div>
                <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Performance Agent</Text>
                    <div><Tag color={currentAgent.isPerformance ? 'green' : 'default'}>{currentAgent.isPerformance ? 'Yes' : 'No'}</Tag></div>
                </div>
            </div>

           <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f9f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AppstoreOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
    </div>
    <div>
        <Text type="secondary" style={{ fontSize: '12px' }}>Domain Groups</Text>
        <div>
            {agentGroupNames(currentAgent, groups).map((name) => (
                <Tag color="blue" key={name} style={{ marginRight: '8px' }}>{name}</Tag>
            ))}
            {agentGroupNames(currentAgent, groups).length === 0 && (
                <Tag color="default">No Domain Groups</Tag>
            )}
        </div>
    </div>
</div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f9f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <TrophyOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
                </div>
                <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Default Tier Eligible</Text>
                    <div><Tag color={currentAgent.isDefaultTierEligible ? 'green' : 'red'}>{currentAgent.isDefaultTierEligible ? 'Yes' : 'No'}</Tag></div>
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f9f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <StarOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
                </div>
                <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Premium Agent</Text>
                    <div><Tag color={currentAgent.premium ? 'gold' : 'default'}>{currentAgent.premium ? 'Yes' : 'No'}</Tag></div>
                </div>
            </div>
        </>
    )}
</Space>
                    </div>
                </div>

          
                
                {/* Activity History Section */}
                <Card>
            <div style={{ marginBottom: '16px' }}>
                <Title level={4}>Activity History</Title>
                <Text type="secondary">
                    Showing all activities in chronological order
                    <Badge
                        count={calculateActivityCount(lead)}
                        size="small"
                        style={{ marginLeft: '8px', backgroundColor: '#1890ff' }}
                    />
                </Text>
            </div>

            {activities.length > 0 ? (
                <div style={{ height: '60vh', overflowY: 'auto', paddingRight: '8px' }}>
                    <Timeline mode="left">
                        {activities.map((activity, index) => (
                            <Timeline.Item
                                key={index}
                                color={
                                    activity.type === 'payment' ? 'green' :
                                        activity.type === 'quote' ? 'blue' :
                                            activity.type === 'stage' ? 'purple' :
                                                activity.type === 'address' ? 'orange' :
                                                    activity.type === 'yard' ? 'cyan' :
                                                        activity.type === 'transfer' ? 'magenta' :
                                                            activity.type === 'followup' ? 'gold' :
                                                                activity.type === 'order' ? 'success' : 'gray'
                                }
                                dot={activity.icon}
                            >
                                <Card size="small" style={{ marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <Text strong style={{ fontSize: '14px' }}>
                                                    {activity.title}
                                                </Text>
                                          <Tag color={getActivityTagColor(activity.type, activity.stage)} size="small">
    {activity.stage === 'Payment Completed' ? 'Payment Details Received' : activity.stage}
</Tag>
                                            </div>

                                            {activity.description && (
                                                <div style={{
                                                    marginTop: '4px',
                                                    fontSize: '14px',
                                                    color: '#666',
                                                    backgroundColor: '#fafafa',
                                                    padding: '8px',
                                                    borderRadius: '4px'
                                                }}>
                                                    {activity.description}
                                                </div>
                                            )}


                                        </div>

                                        <div>
                                            <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                {formatDate(activity.timestamp)}
                                            </Text>
                                        </div>
                                    </div>

                                </Card>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                </div>
            ) : (
                <Empty
                    description="No activities found"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            )}
        </Card>
            </Card>
        </>
    );
};

export default ActivityHistoryTab;
